import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Building2,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Upload,
  User,
} from 'lucide-react'

import {
  useDirectory,
  useCreateLocation,
  useDeleteLocation,
  useCreateExtension,
  useDeleteExtension,
  useImportDirectory,
  type ExtensionKind,
  type OrgExtension,
  type OrgLocation,
} from '../api/directory'
import { formatPhone } from '../lib/utils'
import { SpinLoader } from '../components/shared/SpinLoader'
import { ErrorCard } from '../components/shared/ErrorCard'

export function DirectoryPage() {
  const { t } = useTranslation('directory')
  const { data, isLoading, isError, refetch } = useDirectory()
  const [showAddLocation, setShowAddLocation] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const createLocation = useCreateLocation()

  const locations = data?.locations ?? []
  const extensions = data?.extensions ?? []

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-text-primary md:text-2xl">
            {t('title')}
          </h1>
          <p className="mt-1 text-sm text-text-secondary">{t('subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowImport((v) => !v)}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-elevated px-3 py-2 text-sm text-text-secondary transition-colors hover:bg-surface-card"
          >
            <Upload className="h-4 w-4" />
            {t('import.button')}
          </button>
          <button
            onClick={() => setShowAddLocation((v) => !v)}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            {t('locations.add')}
          </button>
        </div>
      </div>

      {showImport && <ImportBox onDone={() => setShowImport(false)} />}

      {showAddLocation && (
        <LocationForm
          onSubmit={async (payload) => {
            await createLocation.mutateAsync(payload)
            setShowAddLocation(false)
          }}
          onCancel={() => setShowAddLocation(false)}
          isLoading={createLocation.isPending}
          error={createLocation.error ? t('errors.generic') : undefined}
        />
      )}

      {isLoading && <SpinLoader label={t('loading')} />}
      {isError && <ErrorCard message={t('errors.loadFailed')} onRetry={() => refetch()} />}

      {data && locations.length === 0 && (
        <div className="rounded-lg border border-border bg-surface-card p-8 text-center">
          <Building2 className="mx-auto h-10 w-10 text-text-muted" />
          <p className="mt-3 text-sm text-text-muted">{t('locations.empty')}</p>
        </div>
      )}

      {data && locations.length > 0 && (
        <div className="space-y-3">
          {locations.map((loc) => (
            <LocationBlock
              key={loc.id}
              location={loc}
              extensions={extensions.filter((e) => e.location_id === loc.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function LocationBlock({
  location,
  extensions,
}: {
  location: OrgLocation
  extensions: OrgExtension[]
}) {
  const { t } = useTranslation('directory')
  const [open, setOpen] = useState(true)
  const [showAddExt, setShowAddExt] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const createExtension = useCreateExtension()
  const deleteLocation = useDeleteLocation()

  return (
    <div className="rounded-lg border border-border bg-surface-card">
      <div className="flex flex-wrap items-center justify-between gap-2 p-4">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-left"
        >
          {open ? (
            <ChevronDown className="h-4 w-4 text-text-muted" />
          ) : (
            <ChevronRight className="h-4 w-4 text-text-muted" />
          )}
          <span className="font-semibold text-text-primary">
            {location.label || <span className="italic text-text-muted">{t('locations.unnamed')}</span>}
          </span>
          {location.main_phone && (
            <span className="font-mono text-xs text-text-muted">
              {formatPhone(location.main_phone)}
            </span>
          )}
          <span className="rounded-full bg-surface-elevated px-2 py-0.5 text-xs text-text-muted">
            {t('locations.extCount', { count: extensions.length })}
          </span>
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddExt((v) => !v)}
            className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs text-text-secondary transition-colors hover:bg-surface-elevated"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('extensions.add')}
          </button>
          <button
            onClick={() => setConfirmDelete(true)}
            aria-label={t('locations.delete')}
            className="rounded-lg border border-error/30 p-1.5 text-error transition-colors hover:bg-error/10"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {confirmDelete && (
        <div className="mx-4 mb-4 rounded-lg border border-error/30 bg-error/5 p-3">
          <p className="text-sm text-text-primary">{t('locations.deleteConfirm')}</p>
          <div className="mt-2 flex gap-2">
            <button
              onClick={async () => {
                await deleteLocation.mutateAsync(location.id)
                setConfirmDelete(false)
              }}
              disabled={deleteLocation.isPending}
              className="rounded-lg bg-error px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-error/90 disabled:opacity-50"
            >
              {t('locations.deleteConfirmYes')}
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="rounded-lg border border-border px-3 py-1.5 text-sm text-text-secondary transition-colors hover:bg-surface-elevated"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {open && (
        <div className="border-t border-border px-4 py-3">
          {showAddExt && (
            <ExtensionForm
              locationId={location.id}
              onSubmit={async (payload) => {
                await createExtension.mutateAsync(payload)
                setShowAddExt(false)
              }}
              onCancel={() => setShowAddExt(false)}
              isLoading={createExtension.isPending}
              error={createExtension.error ? t('errors.generic') : undefined}
            />
          )}
          {extensions.length === 0 ? (
            <p className="py-2 text-sm italic text-text-muted">{t('extensions.empty')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-text-muted">
                  <th className="py-2 pr-4">{t('extensions.columns.ext')}</th>
                  <th className="py-2 pr-4">{t('extensions.columns.label')}</th>
                  <th className="py-2 pr-4">{t('extensions.columns.description')}</th>
                  <th className="py-2 pr-4">{t('extensions.columns.kind')}</th>
                  <th className="py-2 pr-4">{t('extensions.columns.dialTarget')}</th>
                  <th className="py-2 pr-4">{t('extensions.columns.user')}</th>
                  <th className="py-2 pr-4">{t('extensions.columns.sendDigits')}</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {extensions.map((ext) => (
                  <ExtensionRow key={ext.id} ext={ext} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

function ExtensionRow({ ext }: { ext: OrgExtension }) {
  const { t } = useTranslation('directory')
  const deleteExtension = useDeleteExtension()
  return (
    <tr className="border-b border-border/50">
      <td className="py-2 pr-4 font-mono text-text-secondary">{ext.extension}</td>
      <td className="py-2 pr-4 text-text-primary">{ext.label || '—'}</td>
      <td className="py-2 pr-4 text-text-secondary">{ext.description || '—'}</td>
      <td className="py-2 pr-4 text-text-secondary">{t(`extensions.kinds.${ext.kind}`)}</td>
      <td className="py-2 pr-4 font-mono text-text-muted">
        {ext.location_main_phone ? (
          <>
            {formatPhone(ext.location_main_phone)}
            <span className="text-text-secondary"> ×{ext.extension}</span>
          </>
        ) : (
          '—'
        )}
      </td>
      <td className="py-2 pr-4 text-text-muted">
        {ext.user_id ? (
          <span className="inline-flex items-center gap-1">
            <User className="h-3 w-3" />
            {ext.user_id}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="py-2 pr-4 font-mono text-text-muted">{ext.send_digits || '—'}</td>
      <td className="py-2 text-right">
        <button
          onClick={() => deleteExtension.mutate(ext.id)}
          aria-label={t('extensions.delete')}
          className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-error/10 hover:text-error"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  )
}

function LocationForm({
  onSubmit,
  onCancel,
  isLoading,
  error,
}: {
  onSubmit: (data: { label: string; address: string; main_phone: string }) => void
  onCancel: () => void
  isLoading: boolean
  error?: string
}) {
  const { t } = useTranslation('directory')
  const [label, setLabel] = useState('')
  const [address, setAddress] = useState('')
  const [mainPhone, setMainPhone] = useState('')

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit({ label: label.trim(), address: address.trim(), main_phone: mainPhone.trim() })
      }}
      className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3"
    >
      <h3 className="text-sm font-semibold text-text-primary">{t('locations.formTitle')}</h3>
      {error && <p className="text-sm text-error">{error}</p>}
      <div className="grid gap-3 sm:grid-cols-3">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t('locations.labelPlaceholder')}
          className={inputClass}
        />
        <input
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder={t('locations.addressPlaceholder')}
          className={inputClass}
        />
        <input
          value={mainPhone}
          onChange={(e) => setMainPhone(e.target.value)}
          placeholder={t('locations.mainPhonePlaceholder')}
          className={`${inputClass} font-mono`}
        />
      </div>
      <FormButtons isLoading={isLoading} onCancel={onCancel} />
    </form>
  )
}

function ExtensionForm({
  locationId,
  onSubmit,
  onCancel,
  isLoading,
  error,
}: {
  locationId: string
  onSubmit: (data: {
    location_id: string
    extension: string
    label: string
    kind: ExtensionKind
    user_id: string | null
    send_digits: string | null
    description: string | null
  }) => void
  onCancel: () => void
  isLoading: boolean
  error?: string
}) {
  const { t } = useTranslation('directory')
  const [extension, setExtension] = useState('')
  const [label, setLabel] = useState('')
  const [kind, setKind] = useState<ExtensionKind>('department')
  const [userId, setUserId] = useState('')
  const [sendDigits, setSendDigits] = useState('')
  const [description, setDescription] = useState('')

  const canSubmit = extension.trim().length > 0

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (!canSubmit) return
        onSubmit({
          location_id: locationId,
          extension: extension.trim(),
          label: label.trim(),
          kind,
          user_id: userId.trim() || null,
          send_digits: sendDigits.trim() || null,
          description: description.trim() || null,
        })
      }}
      className="mb-3 rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3"
    >
      <h3 className="text-sm font-semibold text-text-primary">{t('extensions.formTitle')}</h3>
      {error && <p className="text-sm text-error">{error}</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        <input
          value={extension}
          onChange={(e) => setExtension(e.target.value)}
          placeholder={t('extensions.extPlaceholder')}
          required
          className={`${inputClass} font-mono`}
        />
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t('extensions.labelPlaceholder')}
          className={inputClass}
        />
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as ExtensionKind)}
          className={inputClass}
        >
          <option value="department">{t('extensions.kinds.department')}</option>
          <option value="person">{t('extensions.kinds.person')}</option>
        </select>
        <input
          value={sendDigits}
          onChange={(e) => setSendDigits(e.target.value)}
          placeholder={t('extensions.sendDigitsPlaceholder')}
          className={`${inputClass} font-mono`}
        />
        <input
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder={t('extensions.userIdPlaceholder')}
          className={`${inputClass} sm:col-span-2`}
        />
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('extensions.descriptionPlaceholder')}
          className={`${inputClass} sm:col-span-2`}
        />
      </div>
      <p className="text-xs text-text-muted">{t('extensions.userIdHint')}</p>
      <FormButtons isLoading={isLoading} onCancel={onCancel} disabled={!canSubmit} />
    </form>
  )
}

function ImportBox({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation('directory')
  const [text, setText] = useState('')
  const importDirectory = useImportDirectory()
  const [result, setResult] = useState<string | null>(null)

  const submit = async () => {
    if (!text.trim()) return
    const res = await importDirectory.mutateAsync(text)
    setResult(
      t('import.result', {
        locations: res.created_locations,
        extensions: res.created_extensions,
      }),
    )
    setText('')
  }

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3">
      <h3 className="text-sm font-semibold text-text-primary">{t('import.title')}</h3>
      <p className="text-xs text-text-muted whitespace-pre-line">{t('import.help')}</p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={8}
        placeholder={t('import.placeholder')}
        className="w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono text-sm text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      />
      {importDirectory.isError && <p className="text-sm text-error">{t('errors.generic')}</p>}
      {result && <p className="text-sm text-success">{result}</p>}
      <div className="flex gap-2">
        <button
          onClick={submit}
          disabled={importDirectory.isPending || !text.trim()}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {importDirectory.isPending ? t('import.importing') : t('import.submit')}
        </button>
        <button
          onClick={onDone}
          className="rounded-lg border border-border px-4 py-2 text-sm text-text-secondary transition-colors hover:bg-surface-elevated"
        >
          {t('common.done')}
        </button>
      </div>
    </div>
  )
}

function FormButtons({
  isLoading,
  onCancel,
  disabled,
}: {
  isLoading: boolean
  onCancel: () => void
  disabled?: boolean
}) {
  const { t } = useTranslation('directory')
  return (
    <div className="flex gap-2">
      <button
        type="submit"
        disabled={isLoading || disabled}
        className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
      >
        {isLoading ? t('common.saving') : t('common.save')}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="rounded-lg border border-border px-4 py-2 text-sm text-text-secondary transition-colors hover:bg-surface-elevated"
      >
        {t('common.cancel')}
      </button>
    </div>
  )
}

const inputClass =
  'rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
